/**
 * Artifact Resolver — reverse-lookup path to artifact_id using navigation-index.
 *
 * Uses glob matching from guardrail-hooks.ts to match file paths against
 * artifact path_patterns defined in the navigation index.
 */

import { matchGlob } from "../guardrail-hooks.js";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Minimal shape of a navigation-index artifact node. */
export interface NavigationArtifact {
  properties?: {
    type?: string;
    authority?: string;
    manual_edit?: string;
    [key: string]: unknown;
  };
  files?: {
    path_patterns?: string[];
    [key: string]: unknown;
  };
  agents?: {
    owners?: string[];
    editors?: string[];
    [key: string]: unknown;
  };
  routes?: {
    regenerate?: string;
    [key: string]: unknown;
  };
  relations?: {
    derived_artifacts?: string[];
    [key: string]: unknown;
  };
}

export interface ArtifactMatch {
  artifactId: string;
  artifact: NavigationArtifact;
  /** The glob pattern that matched. */
  matchedPattern: string;
}

// ---------------------------------------------------------------------------
// Resolver
// ---------------------------------------------------------------------------

/**
 * Resolve a file path to its artifact(s) in the navigation index.
 * Returns all matching artifacts (a file may belong to multiple artifacts).
 */
export function resolvePathToArtifacts(
  filePath: string,
  navigationIndex: Record<string, unknown>,
): ArtifactMatch[] {
  const artifacts =
    (navigationIndex.artifacts as Record<string, NavigationArtifact>) ?? {};
  const matches: ArtifactMatch[] = [];

  for (const [id, artifact] of Object.entries(artifacts)) {
    const patterns = artifact?.files?.path_patterns;
    if (!Array.isArray(patterns)) continue;

    for (const pattern of patterns) {
      if (typeof pattern !== "string") continue;
      if (matchGlob(pattern, filePath)) {
        matches.push({ artifactId: id, artifact, matchedPattern: pattern });
        break; // One match per artifact is sufficient
      }
    }
  }

  return matches;
}

/**
 * Check if a file path targets a protected artifact (generated or manual_edit: forbidden).
 */
export function isProtectedArtifact(match: ArtifactMatch): {
  protected: boolean;
  reason: string;
} {
  const props = match.artifact.properties ?? {};

  if (props.authority === "generated") {
    const regen = match.artifact.routes?.regenerate;
    const hint = regen
      ? ` Run '${regen}' to regenerate.`
      : " Edit the source artifact and regenerate instead.";
    return {
      protected: true,
      reason:
        `File belongs to generated artifact '${match.artifactId}'.` +
        ` Direct editing is not allowed.${hint}`,
    };
  }

  if (props.manual_edit === "forbidden") {
    return {
      protected: true,
      reason:
        `File belongs to artifact '${match.artifactId}' which has manual_edit: forbidden.` +
        ` This artifact must not be edited directly.`,
    };
  }

  return { protected: false, reason: "" };
}

/**
 * Get all artifact entries from a navigation index as a flat list.
 */
export function listArtifacts(
  navigationIndex: Record<string, unknown>,
): Array<{ id: string; artifact: NavigationArtifact }> {
  const artifacts =
    (navigationIndex.artifacts as Record<string, NavigationArtifact>) ?? {};
  return Object.entries(artifacts).map(([id, artifact]) => ({
    id,
    artifact,
  }));
}
